// DiaryTabBar — horizontal tab strip for the seven days of the diary week.
//
// One tab per day. The tab carries:
//   - day-of-week label (Monday ...)
//   - booking-count badge
//   - small "changed" dot when the day has unsaved local-order edits
//
// The tab bar is purely presentational — clicking calls onChange with the
// day's date (YYYY-MM-DD). The page owns the active-tab state.

import { cn } from '../../../utils/cn';
import styles from './DiaryTabBar.module.css';

export interface DiaryTab {
  date: string; // YYYY-MM-DD
  dayOfWeek: string; // 'Monday' ... 'Sunday'
  bookingCount: number;
  /** True when this day's local order differs from the server's. */
  changed: boolean;
  /** True when the driver is on holiday on this day. */
  holiday?: boolean;
}

export interface DiaryTabBarProps {
  tabs: DiaryTab[];
  activeDate: string;
  onChange: (date: string) => void;
  className?: string;
}

export function DiaryTabBar({
  tabs,
  activeDate,
  onChange,
  className,
}: DiaryTabBarProps): JSX.Element {
  return (
    <nav
      role="tablist"
      aria-label="Diary days"
      className={cn(styles.bar, className)}
    >
      {tabs.map((tab) => {
        const isActive = tab.date === activeDate;
        const isZero = tab.bookingCount === 0;
        return (
          <button
            key={tab.date}
            type="button"
            role="tab"
            id={`diary-tab-${tab.date}`}
            aria-selected={isActive}
            aria-controls={`diary-panel-${tab.date}`}
            tabIndex={isActive ? 0 : -1}
            className={cn(
              styles.tab,
              isActive && styles.tabActive,
              tab.changed && styles.tabChanged,
              tab.holiday && styles.tabHoliday,
              tab.holiday && isActive && styles.tabHolidayActive,
            )}
            data-holiday={tab.holiday ? 'true' : undefined}
            onClick={() => onChange(tab.date)}
          >
            <span className={styles.label}>{tab.dayOfWeek}</span>
            <span
              className={cn(
                styles.badge,
                isActive && styles.badgeActive,
                isZero && styles.badgeZero,
              )}
              aria-label={`${tab.bookingCount} bookings`}
            >
              {tab.bookingCount}
            </span>
          </button>
        );
      })}
    </nav>
  );
}
